/**
 * TABLET-SETUP-MODAL
 * Prompts tablet users to enable fullscreen + landscape + wake lock
 */
import { useEffect, useRef, useState } from "react"
import Button from "./button"
import Modal from "./modal"
import isTablet from "@/utils/is-tablet.utils"
import Space from "./space"

async function enterFullscreen() {
  if (!document.fullscreenElement) {
    await document.documentElement.requestFullscreen()
  }
}

/**
 * Try to lock screen orientation to landscape (best-effort)
 */
async function lockLandscape() {
  try {
    // Some browsers require fullscreen to allow orientation lock
    const screenAny = screen as any
    if (screenAny.orientation?.lock) {
      await screenAny.orientation.lock("landscape")
    }
  } catch {
    // Not supported or blocked -> ignore
  }
}

/**
 * Keep screen awake (best-effort)
 */
async function requestWakeLock() {
  try {
    const navAny = navigator as any
    if (!navAny.wakeLock?.request) return null
    return await navAny.wakeLock.request("screen")
  } catch {
    return null
  }
}

export default function TabletSetupModal() {
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement)
  const wakeLockRef = useRef<any>(null)

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener("fullscreenchange", onFsChange)
    return () => document.removeEventListener("fullscreenchange", onFsChange)
  }, [])

  // Re-acquire wake lock when coming back to the tab (wake lock can be released)
  useEffect(() => {
    const onVisibility = async () => {
      if (document.visibilityState !== "visible") return
      if (!wakeLockRef.current) return
      wakeLockRef.current = await requestWakeLock()
    }

    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [])

  const showModal = isTablet() && !isFullscreen
  if (!showModal) return null

  return (
    <Modal zIndex={99999}>
      <div className="flex h-full">
        <div className="m-auto">
          <p>
            <b>{`L'application IDTree est développée pour fonctionner en plein écran.`}</b>
          </p>
          <div className="flex">
            <Space />
            <img src={require("@/assets/owl.png")} alt="owl" style={{ width: 130, height: "auto" }} />
            <Space />
          </div>
          <div className="flex">
            <Space />
            <Button
              isLarge
              isFullWidth
              isWithBorder
              className="primary"
              onClick={async () => {
                await enterFullscreen()
                await lockLandscape()
                wakeLockRef.current = await requestWakeLock()
              }}>
              Passer en plein écran
            </Button>
            <Space />
          </div>
        </div>
      </div>
    </Modal>
  )
}
